// ============================================================
// LONG SESSION TRACKER — Fase 12.9
// Rastreia saúde de sessões longas (6h, 12h, 24h)
// Métricas: uptime, delta de memória, reconnects/h, FPS médio
// ============================================================
import { obs } from '@/core/observability'
import { useTVHealthStore } from '@/store/tvHealthStore'

// ─── Types ────────────────────────────────────────────────
export interface SessionCheckpoint {
  elapsedMs:        number
  heapMB:           number
  heapDeltaMB:      number    // vs session start
  reconnectsTotal:  number
  errorsTotal:      number
  avgFPS:           number
  renderStability:  number    // 0-100 (100 = stable)
  heapGrowthPerHr:  number    // MB/hour projected
  reconnectsPerHr:  number
  errorsPerHr:      number
}

export interface SessionReport {
  sessionId:     string
  startedAt:     string
  checkpoints:   SessionCheckpoint[]
  latest:        SessionCheckpoint | null
  assessment:    SessionAssessment
}

export interface SessionAssessment {
  stable6h:   boolean
  stable12h:  boolean
  stable24h:  boolean
  risks:      string[]
  score:      number    // 0-100
}

// ─── State ────────────────────────────────────────────────
let _sessionId   = generateSessionId()
let _startMs     = Date.now()
let _startHeap   = 0
let _reconnects  = 0
let _errors      = 0
const _fpsSamples: number[] = []
const _heapSamples: { ts: number; mb: number }[] = []
const _checkpoints: SessionCheckpoint[] = []
let _timer: ReturnType<typeof setInterval> | null = null
const _listeners: Array<(r: SessionReport) => void> = []

// ─── Public instrumentation ───────────────────────────────
export function recordReconnect(): void  { _reconnects++ }
export function recordError(): void      { _errors++ }
export function recordFPS(fps: number): void {
  _fpsSamples.push(fps)
  if (_fpsSamples.length > 360) _fpsSamples.shift()  // last 6min at 1Hz
}

// ─── Session control ──────────────────────────────────────
export function startLongSessionTracker(checkpointIntervalMs = 60_000): () => void {
  _sessionId  = generateSessionId()
  _startMs    = Date.now()
  _reconnects = 0
  _errors     = 0
  _fpsSamples.length     = 0
  _heapSamples.length    = 0
  _checkpoints.length    = 0

  // Capture initial heap
  _startHeap = currentHeapMB()

  // Subscribe to tvHealthStore FPS updates (available when TV is running)
  const unsubFPS = useTVHealthStore.subscribe(
    s => s.fps,
    (fps) => { if (fps > 0) recordFPS(fps) },
  )

  if (_timer) clearInterval(_timer)
  _timer = setInterval(() => {
    const cp = captureCheckpoint()
    _checkpoints.push(cp)
    // Keep last 1440 checkpoints (24h at 1/min)
    if (_checkpoints.length > 1440) _checkpoints.shift()

    const report = buildReport()
    _listeners.forEach(fn => fn(report))

    if (!report.assessment.stable6h) {
      obs.warn('session_tracker', `Session instability detected`, {
        elapsedHr: (cp.elapsedMs / 3_600_000).toFixed(1),
        score:     report.assessment.score,
        risks:     report.assessment.risks.join(', '),
      })
    }
  }, checkpointIntervalMs)

  obs.info('session_tracker', `Long session tracker started (${_sessionId})`)

  return () => {
    if (_timer) { clearInterval(_timer); _timer = null }
    unsubFPS()
    obs.info('session_tracker', `Long session ended: ${formatDuration(Date.now() - _startMs)}`)
  }
}

export function onSessionReport(fn: (r: SessionReport) => void): () => void {
  _listeners.push(fn)
  return () => {
    const i = _listeners.indexOf(fn)
    if (i !== -1) _listeners.splice(i, 1)
  }
}

export function getSessionReport(): SessionReport {
  return buildReport()
}

// ─── Capture ──────────────────────────────────────────────
function captureCheckpoint(): SessionCheckpoint {
  const elapsedMs = Date.now() - _startMs
  const heapMB    = currentHeapMB()
  const heapDelta = heapMB - _startHeap

  _heapSamples.push({ ts: Date.now(), mb: heapMB })
  if (_heapSamples.length > 60) _heapSamples.shift()

  const elapsedHr = Math.max(elapsedMs / 3_600_000, 1 / 3600)

  return {
    elapsedMs,
    heapMB,
    heapDeltaMB:     heapDelta,
    reconnectsTotal: _reconnects,
    errorsTotal:     _errors,
    avgFPS:          avg(_fpsSamples),
    renderStability: computeRenderStability(),
    heapGrowthPerHr: heapDelta / elapsedHr,
    reconnectsPerHr: _reconnects / elapsedHr,
    errorsPerHr:     _errors / elapsedHr,
  }
}

function buildReport(): SessionReport {
  const latest = _checkpoints.at(-1) ?? null
  return {
    sessionId:   _sessionId,
    startedAt:   new Date(_startMs).toISOString(),
    checkpoints: [..._checkpoints],
    latest,
    assessment:  assessSession(latest),
  }
}

function assessSession(cp: SessionCheckpoint | null): SessionAssessment {
  const risks: string[] = []
  let score = 100

  if (!cp) return { stable6h: true, stable12h: true, stable24h: true, risks: [], score: 100 }

  // Heap growth
  if (cp.heapGrowthPerHr > 100) { risks.push(`Heap crescendo ${Math.round(cp.heapGrowthPerHr)}MB/h`); score -= 30 }
  else if (cp.heapGrowthPerHr > 30) { risks.push(`Heap moderado ${Math.round(cp.heapGrowthPerHr)}MB/h`); score -= 10 }

  // Reconnects
  if (cp.reconnectsPerHr > 20) { risks.push(`Reconexões excessivas: ${Math.round(cp.reconnectsPerHr)}/h`); score -= 20 }
  else if (cp.reconnectsPerHr > 5) { score -= 5 }

  // FPS
  if (cp.avgFPS < 20) { risks.push(`FPS crítico: ${Math.round(cp.avgFPS)}`); score -= 25 }
  else if (cp.avgFPS < 45) { risks.push(`FPS baixo: ${Math.round(cp.avgFPS)}`); score -= 10 }

  // Render stability
  if (cp.renderStability < 60) { risks.push(`Instabilidade de render: ${cp.renderStability}%`); score -= 15 }

  // Errors
  if (cp.errorsPerHr > 10) { risks.push(`Erros: ${Math.round(cp.errorsPerHr)}/h`); score -= 10 }

  score = Math.max(0, score)

  return {
    stable6h:   score >= 70,
    stable12h:  score >= 80,
    stable24h:  score >= 90,
    risks,
    score,
  }
}

// ─── Helpers ──────────────────────────────────────────────
function currentHeapMB(): number {
  if (typeof performance === 'undefined' || !(performance as any).memory) return 0
  return Math.round((performance as any).memory.usedJSHeapSize / 1_048_576)
}

function avg(arr: number[]): number {
  if (arr.length === 0) return 0
  return Math.round(arr.reduce((a, b) => a + b, 0) / arr.length)
}

function computeRenderStability(): number {
  if (_fpsSamples.length < 2) return 100
  const mean = avg(_fpsSamples)
  if (mean === 0) return 0
  const variance = _fpsSamples.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / _fpsSamples.length
  const stddev   = Math.sqrt(variance)
  // Stability = 100 - (coefficient of variation * 100), clamped 0-100
  return Math.max(0, Math.min(100, Math.round(100 - (stddev / mean) * 100)))
}

function generateSessionId(): string {
  return `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
}

function formatDuration(ms: number): string {
  const h = Math.floor(ms / 3_600_000)
  const m = Math.floor((ms % 3_600_000) / 60_000)
  return `${h}h ${m}m`
}
